import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import {
  ChevronDownIcon,
  CrossIcon,
  KanbanIcon,
  TableIcon,
} from "../icons/Icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../app/store";
import { fetchDepartmentList } from "../../features/department/departmentSlice";
import { createJob } from "../../features/job/jobSlices";

const viewTypes = [
  {
    id: "table",
    name: "Create New job posting",
    description: "Organize your records on a table",
    icon: TableIcon,
    iconColor: "text-emerald-600",
  },
  {
    id: "kanban",
    name: "Create From Approval",
    description: "Visualize your pipeline",
    icon: KanbanIcon,
    iconColor: "text-red-500",
  },
];

const people = [
  {
    id: 1,
    department_name: "Select a channel",
    color: "text-[#4b5563]",
    department_image: "",
  },
];

const TableView = ({ setFeedbackOpen }: any) => {
  const [selected, setSelected] = useState(people[0]);
  const [teams, setTeams] = useState<any>(null);
  const dispatch = useAppDispatch();
  const { baseId } = useParams<{ baseId: string }>();
  const [userDetails, setUserDetails] = useState<any>(null);
  const bases = useAppSelector(
    (state: RootState) => state?.base?.data?.data?.bases,
  );
  const [documentName, setDocumentName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // teams
  useEffect(() => {
    let isMounted = true;

    if (baseId) {
      const fetchTeams = async () => {
        const storedBase = localStorage.getItem("baseDepartmentData");
        if (storedBase) {
          if (isMounted) setTeams(JSON.parse(storedBase));
        } else {
          try {
            const baseResponse = await dispatch(
              fetchDepartmentList(userDetails?.id || baseId),
            );
            if (isMounted) setTeams(baseResponse.payload);
          } catch (error) {
            console.error("Error fetching base member details:", error);
          }
        }
      };
      fetchTeams();
    }

    return () => {
      isMounted = false;
    };
  }, [baseId, dispatch, userDetails?.id]);

  useEffect(() => {
    if (bases && bases.length > 0) {
      const matchedBase = bases.find(
        (base: { id: number }) => base.id.toString() === baseId,
      );
      if (matchedBase) {
        setUserDetails(matchedBase);
      }
    } else if (baseId) {
      setUserDetails(baseId);
    } else {
      setUserDetails(null);
    }
  }, [baseId, bases]);

  // Handle document creation
  const handleCreateDocument = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!baseId) {
      console.error("Base ID is required");
      return;
    }
    setLoading(true);

    try {
      const parsedBaseId = parseInt(baseId, 10);
      if (isNaN(parsedBaseId)) {
        throw new Error("Invalid base ID");
      }

      const resultAction = await dispatch(
        createJob({
          job_title: documentName,
          job_department_id: selected.id,
          baseId: parsedBaseId,
        }),
      );

      if (createJob.fulfilled.match(resultAction)) {
        toast.success("Job created successfully!");
        setDocumentName("");
        setSelected(people[0]);
        setFeedbackOpen(false);

        const documentId = resultAction.payload.data.data.id;
        localStorage.removeItem("jobActionData");
        localStorage.removeItem("jobByReviewData");
        localStorage.removeItem("jobPipelineStageData");
        localStorage.removeItem("jobPostDetailsData");
        localStorage.removeItem("jobByHiringTeamMembers");
        localStorage.removeItem("activeFiltersJobApplication");
        localStorage.removeItem("activeFiltersJobApplicationKanban");
        localStorage.removeItem("activeFiltersJobReports");
        localStorage.removeItem("cachedJobsDetailsListData");
        localStorage.removeItem("baseScoreCardDataByJob");
        localStorage.removeItem("baseQuestionnaireDataByJob");
        localStorage.removeItem("baseJobListingData");
        localStorage.removeItem("activeFiltersTopics");
        localStorage.removeItem("cachedJobsListData");
        localStorage.removeItem("baseJobScoreCardData");
        localStorage.removeItem("baseScoreCardDataByJob");
        localStorage.removeItem("baseQuestionnaireDataByJob");
        localStorage.removeItem("jobPipelineStageData");
        localStorage.removeItem("activeFiltersJobApplication");
        localStorage.removeItem("activeFiltersJobsListing");
        localStorage.removeItem("activeFiltersJobsDetailsPage");
        localStorage.removeItem("activeFiltersJobApplicationKanban");
        localStorage.removeItem("jobByHiringTeamMembers");
        localStorage.removeItem("jobActionData");
        localStorage.removeItem("jobByReviewData");
        localStorage.removeItem("jobPostDetailsData");
        localStorage.removeItem("JobPipelineDetailsData");
        localStorage.removeItem("activeFiltersBookmark");
        localStorage.removeItem("cachedBookmarkData");
        localStorage.removeItem("forgot_email");
        navigate(`/${baseId}/job/${documentId}/manage/detail`);
      } else {
        throw new Error(resultAction.error?.message || "Failed to create job");
      }
    } catch (err) {
      toast.error("Failed to create job.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedChange = useCallback(
    (newValue: any) => {
      if (selected.id !== newValue.id) {
        setSelected(newValue);
      }
    },
    [selected.id],
  );

  const teamOptions = useMemo(() => {
    return teams?.data || [];
  }, [teams?.data]);

  return (
    <div className="mt-6">
      <form action="#" onSubmit={handleCreateDocument}>
        <div className="mt-4">
          <label className="wg-label inline-flex cursor-pointer items-center gap-1 text-sm font-medium leading-6 mt-1.5">
            Job title
          </label>
          <input
            type="text"
            name="documentName"
            placeholder="Enter document name"
            className="w-full px-3 py-1.5 border mt-1.5 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
          />
        </div>

        <div className="mt-4">
          <Listbox value={selected} onChange={handleSelectedChange} by="id">
            <label className="wg-label inline-flex cursor-pointer items-center gap-1 text-sm font-medium leading-6">
              Department
            </label>
            <div className="relative mt-1.5">
              <ListboxButton className="relative w-full cursor-default border border-gray-200 rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-white-900 ring-inset ring-gray-300 focus:outline-none sm:text-sm sm:leading-6">
                <span className="flex items-center">
                  {/* Use selected.name to display the selected team's name */}
                  {selected ? (
                    <div className="flex items-center gap-x-2">
                      {selected.department_image ? (
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/${selected.department_image}`}
                          alt={`${selected.department_name || "Profile"}`}
                          className="h-5 w-5 flex-none rounded-full ring-1 ring-gray-900/10"
                        />
                      ) : (
                        <span
                          className="bg-[#a3a3a3] flex items-center justify-center h-5 w-5 flex-none rounded-full ring-1 ring-gray-900/10
                                                        text-white text-[14px] font-medium capitalize line-clamp-1"
                        >
                          {selected.department_name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span
                        className={`block truncate capitalize ${selected.color}`}
                      >
                        {selected.department_name}
                      </span>
                    </div>
                  ) : (
                    "Select a team"
                  )}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDownIcon />
                </span>
              </ListboxButton>

              <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-opacity-5 focus:outline-none">
                {teamOptions.map((person: any) => (
                  <ListboxOption
                    key={person.id}
                    value={person}
                    className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-[#374151] data-[focus]:bg-gray-200 data-[focus]:text-[#374151] text-[14px]"
                  >
                    <span className="flex items-center gap-x-3">
                      {person.department_image ? (
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/${person.department_image}`}
                          alt={`${person.department_name || "Profile"}`}
                          className="h-6 w-6 flex-none rounded-full ring-1 ring-gray-900/10"
                        />
                      ) : (
                        <span
                          className="bg-[#a3a3a3] flex items-center justify-center h-6 w-6 flex-none rounded-full ring-1 ring-gray-900/10
                                          text-white text-[14px] font-medium capitalize line-clamp-1"
                        >
                          {person.department_name?.charAt(0).toUpperCase()}
                        </span>
                      )}

                      <span className={`block truncate capitalize text-[14px]`}>
                        {person.department_name}
                      </span>
                    </span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Listbox>
        </div>
        <span className="border-b border-solid border-[#e3e3e3] mx-[-16px] block py-[6px]"></span>
        <div className="flex items-center justify-end gap-[22px] pt-3 flex-wrap md:flex-nowrap lg:flex-nowrap">
          <button
            type="submit"
            className="group inline-flex shrink-0 w-fit select-none justify-center items-center px-[14px] py-1 text-[13px] font-medium h-[32px] leading-6 transition-colors
                 duration-100 wg-antialiased focus:outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none gap-0 
               rounded-md text-white outline-indigo-600
                 disabled:opacity-50 bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
          >
            {loading ? (
              <div className="flex items-center  gap-[6px]">
                Create
                <div className="simple-spinner">
                  <span></span>
                </div>
              </div>
            ) : (
              " Create"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

const KanbanView = ({ setFeedbackOpen }: any) => {
  return (
    <div className="mt-6">
      <div className="mt-4">
        <label className="wg-label inline-flex cursor-pointer items-center gap-1 text-sm font-medium leading-6 mt-1.5">
          Template Name
        </label>
        <input
          type="text"
          placeholder="Enter a template name"
          className="w-full px-3 py-1.5 border mt-1.5 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <span className="border-b border-solid border-[#e3e3e3] mx-[-16px] block py-[6px]"></span>
      <div className="flex items-center justify-end gap-[22px] pt-3 flex-wrap md:flex-nowrap lg:flex-nowrap">
        <button
          type="submit"
          className="inline-flex items-center relative justify-center whitespace-nowrap rounded-md bg-indigo-600 font-medium leading-normal help-btn-setting text-[#fefeff] min-w-[32px] h-[32px] px-[14px] text-[0.8125rem]"
          // onClick={() => setFeedbackOpen(false)}
        >
          <div className="flex items-center flex-row-reverse gap-[6px]">
            Create
          </div>
        </button>
      </div>
    </div>
  );
};

const CreateJob = () => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectedView, setSelectedView] = useState("table");
  const [openFeedbackTab, setOpenFeedbackTab] = useState("Feedback");
  const navigate = useNavigate();
  const { baseId } = useParams<{ baseId: string }>();

  const handleFeedbacksModal = () => {
    // setFeedbackOpen(true);
    navigate(`/${baseId}/newjob`);
  };

  const renderViewContent = () => {
    switch (selectedView) {
      case "table":
        return <TableView setFeedbackOpen={setFeedbackOpen} />;
      case "kanban":
        return <KanbanView />;
      default:
        return <TableView setFeedbackOpen={setFeedbackOpen} />;
    }
  };

  return (
    <>
      <Toaster richColors />

      <div className="flex flex-col px-[0rem] mt-[0rem]">
        <button
          className="group inline-flex shrink-0 select-none items-center transition-colors duration-100 wg-antialiased focus:outline-0
                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none gap-0 px-8px py-1 
                     rounded-md text-white outline-primary disabled:opacity-50  hover:bg-indigo-500 bg-indigo-600 h-[28px]
                      justify-start [&>span]:flex [&>span]:items-center [&>span]:gap-[6px] font-medium text-[.8125rem] leading-6 px-[14px]"
          onClick={handleFeedbacksModal}
        >
          <span className="px-0 capitalize">
            {/* <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#232529"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-circle-plus w-[1rem] h-[1rem] text-black-100"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
              <path d="M9 12h6" />
              <path d="M12 9v6" />
            </svg> */}
            Create job posting
          </span>
        </button>
      </div>
      <Transition show={feedbackOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 md:z-10 lg:z-10"
          onClose={() => setFeedbackOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center p-4 text-center sm:items-start h-full items-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white px-4 py-4 text-left shadow-xl transition-all sm:mb-8 sm:mt-[32px] sm:w-full sm:max-w-[680px]">
                  <div className="absolute right-0 top-0 hidden pr-4 pt-6 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={() => setFeedbackOpen(false)}
                    >
                      <span className="sr-only">Close</span>
                      <CrossIcon />
                    </button>
                  </div>

                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                      {openFeedbackTab === "Feedback" && (
                        <motion.div
                          className="mt-10"
                          initial={{ opacity: 1, y: 0 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8 }}
                        >
                          <div className="w-full max-w-2xl">
                            <div className="space-y-2">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {viewTypes.map((viewType) => {
                                  const Icon = viewType.icon;
                                  return (
                                    <button
                                      key={viewType.id}
                                      onClick={() =>
                                        setSelectedView(viewType.id)
                                      }
                                      className={`flex items-start p-4 gap-3 rounded-lg border transition-all duration-200 hover:border-gray-300 ${
                                        selectedView === viewType.id
                                          ? "border-1 border-indigo-500 bg-blue-50"
                                          : "border-gray-200"
                                      }`}
                                    >
                                      <div
                                        className={`p-2 rounded-lg ${viewType.iconColor} bg-opacity-10`}
                                      >
                                        <Icon
                                          className={`w-5 h-5 ${viewType.iconColor}`}
                                        />
                                      </div>
                                      <div className="text-left">
                                        <h3 className="font-medium text-gray-900">
                                          {viewType.name}
                                        </h3>
                                        <p className="text-sm text-gray-500">
                                          {viewType.description}
                                        </p>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* View content */}
                            {renderViewContent()}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CreateJob;
